import { lazy } from 'react'

/*
 * React.lazy that survives a deploy.
 *
 * Chunks are content-hashed, so a tab holding an older index.html asks for filenames
 * the new build no longer contains. <Suspense> only handles a *pending* import — a
 * *rejected* one propagates as a render error and, without a boundary above it, takes
 * the whole tree down (the blank screen this was written for).
 *
 * On a chunk failure we reload once: the fresh index.html points at chunks that exist.
 * The one-shot guard is the important part — if the reload does not help (a CDN still
 * serving a stale index.html, or a file genuinely missing from the bucket) we must
 * surface an error rather than reload forever.
 */

const RETRY_PREFIX = 'chunkRetry:'

// sessionStorage throws, not returns null, in some privacy modes and sandboxed frames.
// A browser we cannot read from just means no retry — never a crash.
const session = {
    get(key) {
        try {
            return window.sessionStorage.getItem(key)
        } catch {
            return null
        }
    },
    set(key, value) {
        try {
            window.sessionStorage.setItem(key, value)
            return true
        } catch {
            return false
        }
    },
    remove(key) {
        try {
            window.sessionStorage.removeItem(key)
        } catch {
            /* nothing to clean up if we cannot reach storage */
        }
    },
}

// webpack names these ChunkLoadError, but the message is the only reliable signal
// across browsers, and mini-css-extract fails the same way for `.chunk.css`.
const CHUNK_ERROR_MESSAGE = /loading (css )?chunk .* failed|failed to fetch dynamically imported module|error loading dynamically imported module/i

export function isChunkLoadError(error) {
    if (!error) return false
    if (error.name === 'ChunkLoadError') return true
    return CHUNK_ERROR_MESSAGE.test(String(error.message || ''))
}

/**
 * The loading strategy, separated from React.lazy so it can be exercised directly.
 *
 * @param {() => Promise<{ default: React.ComponentType }>} factory the dynamic import
 * @param {string} key stable identifier for this chunk; the import path is a good one
 */
export function createChunkLoader(factory, key) {
    const retryKey = `${RETRY_PREFIX}${key}`

    return async () => {
        try {
            const module = await factory()
            // Import succeeded, so whatever went wrong earlier is resolved. Clearing
            // this re-arms the retry for the *next* deploy in this same session.
            session.remove(retryKey)
            return module
        } catch (error) {
            // A genuine crash inside the module is not a stale-chunk problem. Let it
            // reach the ErrorBoundary (and Sentry) untouched.
            if (!isChunkLoadError(error)) throw error

            if (session.get(retryKey)) throw error

            // If we cannot record the attempt we cannot bound the retries, so don't start.
            if (!session.set(retryKey, String(Date.now()))) throw error

            window.location.reload()

            // Keep the component suspended on its existing skeleton while the browser
            // tears the page down; resolving or rejecting here would flash UI first.
            return new Promise(() => {})
        }
    }
}

export default function lazyWithRetry(factory, key) {
    return lazy(createChunkLoader(factory, key))
}
